import React, { useState, useRef, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useSelectedData } from '../contexts/SelectedContext';

const FIXED_HINT_KEYS = [
  'eu_aut_date',
  'atc_code_nameL1',
  'atc_code_nameL2',
  'atc_code_nameL3',
  'eu_mah_current'
];

/**
 * Generates hints using the predefined list of keys.
 * Filters out hints where the value is null, 'NA', or 'False'.
 */
const generateHints = (medicine) => {
  if (!medicine) return [];

  // Use only the fixed keys and filter out null/NA/False values
  return FIXED_HINT_KEYS.map(key => {
    const value = medicine[key];
    if (value && value !== 'NA' && value !== 'False') {
      const formattedKey = key.replace(/_/g, ' ').toUpperCase();
      return `${formattedKey}: ${value}`;
    }
    return null;
  }).filter(hint => hint !== null);
};

/**
 * Masks an array of names within a given text, replacing them with 'XXXX'.
 * This is used to mask both Brand Name and Active Substance (INN).
 */
const maskNames = (text, namesToMask) => {
  if (!text || !namesToMask || namesToMask.length === 0) return text;
  let maskedText = text;
  for (const name of namesToMask) {
    if (name && name !== 'NA') {
      // Escape special characters for regex safety
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Create regex for the name (case insensitive, global)
      const regex = new RegExp(`(${escapedName})`, 'gi');
      maskedText = maskedText.replace(regex, 'XXXX');
    }
  }
  return maskedText;
};

const modes = {
  NORMAL: {
    label: 'All Medicines',
    filter: med => true,
    emoji: ''
  },
  ATMP: {
    label: 'ATMP Mode',
    filter: med => med.eu_atmp === 'True',
    emoji: '🧬'
  },
  ONCOLOGY: {
    label: 'Oncology Mode',
    filter: med =>
      med.atc_code?.startsWith('L01') ||
      med.atc_code?.startsWith('L02') ||
      med.atc_code?.startsWith('L03A'),
    emoji: '🎗️'
  },
  EMRD_SELECTION: {
    label: 'Use EMRD Selection',
    // The filter will be applied in the useEffect, using the actual selectedData array
    filter: (med, selectedDataKeys) => selectedDataKeys.has(med.eu_pnumber),
    emoji: ''
  }
};

const MedicineGuessGame = () => {
  const data = useData();
  const { selectedData } = useSelectedData();
  const [allMedicines, setAllMedicines] = useState([]);
  const [allNames, setAllNames] = useState([]);
  const [activeMode, setActiveMode] = useState('NORMAL');
  const [guessType, setGuessType] = useState('inn');

  const [currentMedicine, setCurrentMedicine] = useState(null);
  const [userGuess, setUserGuess] = useState('');
  const [message, setMessage] = useState('');
  const [revealedHints, setRevealedHints] = useState([]);
  const [guessCount, setGuessCount] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hints, setHints] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);

  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  const currentMode = modes[activeMode] || modes.NORMAL;

  const toggleShowAnswer = () => setShowAnswer(prev => !prev);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter medicines based on active mode
  useEffect(() => {
    if (!data) return;

    let filtered = [...data];

    if (activeMode === 'EMRD_SELECTION') {
      const selectedKeys = new Set(selectedData.map(med => med.eu_pnumber));
      // Use the eu_pnumber field from the current medicine to check against the Set of selected keys
      filtered = filtered.filter(med => selectedKeys.has(med.eu_pnumber));
    } else {
      const mode = modes[activeMode];
      if (mode?.filter) {
        filtered = filtered.filter(mode.filter);
      }
    }

    if (!filtered || filtered.length === 0) {
      setAllMedicines([]);
      setCurrentMedicine(null);
      setHints([]);
      if (activeMode !== 'NORMAL') {
        setActiveMode('NORMAL');
      }
      return;
    }

    setAllMedicines(filtered);

    // Dynamic name list depending on guess type
    const nameField =
      guessType === 'brand' ? 'eu_brand_name_current' : 'active_substance_current';
    const names = [...new Set(filtered.map(med => med[nameField]))].filter(
      n => n && n !== 'NA'
    );
    setAllNames(names);

    const randomMedicine = filtered[Math.floor(Math.random() * filtered.length)];
    setCurrentMedicine(randomMedicine);
    setHints(generateHints(randomMedicine));
  }, [data, activeMode, guessType, selectedData]);

  const toggleMode = (modeKey) => {
    setActiveMode(prev => (prev === modeKey ? 'NORMAL' : modeKey));
    
    // Clear state on mode switch
    setMessage('');
    setShowAnswer(false);
    setUserGuess('');
    setGuessCount(0);
    setRevealedHints([]);
    setSelectedIndex(-1);
    setShowSuggestions(false);
  };

  const toggleGuessType = () => {
    setGuessType(prev => (prev === 'brand' ? 'inn' : 'brand'));
    
    // Clear state on guess type switch
    setMessage('');
    setShowAnswer(false);
    setUserGuess('');
    setGuessCount(0);
    setRevealedHints([]);
    setSelectedIndex(-1);
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUserGuess(value);
    setSelectedIndex(-1);

    if (value.length > 0) {
      const lowerCaseValue = value.toLowerCase();
      const matchingStart = allNames.filter(name =>
        name.toLowerCase().startsWith(lowerCaseValue)
      );
      const matchingSubstring = allNames.filter(
        name =>
          !name.toLowerCase().startsWith(lowerCaseValue) &&
          name.toLowerCase().includes(lowerCaseValue)
      );
      const combined = matchingStart.concat(matchingSubstring).slice(0, 5);
      setSuggestions(combined);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === 'Enter') checkGuess();
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleSuggestionClick(suggestions[selectedIndex]);
        else checkGuess();
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setUserGuess(suggestion);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current.focus();
  };

  const checkGuess = () => {
    if (!currentMedicine) return;

    const newGuessCount = guessCount + 1;
    setGuessCount(newGuessCount);

    const correctAnswer =
      guessType === 'brand'
        ? currentMedicine.eu_brand_name_current
        : currentMedicine.active_substance_current;

    if (userGuess.trim().toLowerCase() === correctAnswer.toLowerCase()) {
      setMessage(
        `✅ Correct! You guessed the ${guessType === 'brand' ? 'brand' : 'substance'
        } in ${newGuessCount} ${newGuessCount === 1 ? 'guess' : 'guesses'}!`
      );
      setShowAnswer(true); 
    } else {
      setMessage(`❌ Incorrect, try again! (Attempts: ${newGuessCount})`);
    }
  };

  const revealHint = () => {
    if (revealedHints.length < hints.length) {
      setRevealedHints([...revealedHints, hints[revealedHints.length]]);
    }
  };

  const getNewMedicine = () => {
    if (!allMedicines || allMedicines.length === 0) return;
    const newMedicine =
      allMedicines[Math.floor(Math.random() * allMedicines.length)];
    setCurrentMedicine(newMedicine);
    setHints(generateHints(newMedicine));
    setUserGuess('');
    setMessage('');
    setRevealedHints([]);
    setGuessCount(0);
    setShowAnswer(false);
  };

  if (!data) {
    return <div>Loading medicine data...</div>;
  }

  const selectedCount = selectedData.length;

  if (!currentMedicine) {
    if (activeMode === 'EMRD_SELECTION' && selectedCount === 0) {
      return <div>Please select medicines first to use the **Use EMRD Selection** mode.</div>;
    }
    return <div>No medicine data available for the current mode. Please switch to Normal Mode.</div>;
  }

  const answerField =
    guessType === 'brand'
      ? currentMedicine.eu_brand_name_current
      : currentMedicine.active_substance_current;

  const namesToMask = [
    currentMedicine.eu_brand_name_current,
    currentMedicine.active_substance_current,
  ];

  return (
    <div>
      <h1>Guess the Medicine!</h1>

      <p>
        You are given a random indication with the product name and active substance masked (XXXX).
        Guess the <strong>{guessType === 'brand' ? 'Brand Name' : 'Active Substance (INN)'}</strong>!
      </p>
      <hr className="med-top-separator" />

      <div>
        <h3>Mystery Indication:</h3>
        <p>
          {maskNames(
            currentMedicine.eu_indication_current,
            namesToMask
          )}
        </p>
      </div>

      {message && <p className="mt-4 mb-4 font-bold">{message}</p>}

      <div className='mt-4'>
        <div className="relative mb-2">
          <input
            ref={inputRef}
            type="text"
            className="med-text-input"
            placeholder={`Enter ${guessType === 'brand' ? 'brand name' : 'active substance'}`}
            value={userGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={{ marginLeft: '10px', width: '80%' }}
          />

          {showSuggestions && suggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              id="suggestions-list"
              role="listbox"
              className="absolute z-20 w-full mt-1 border rounded shadow-lg "
            >
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  role="option"
                  aria-selected={index === selectedIndex}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`p-3 cursor-pointer ${index === selectedIndex
                      ? 'bg-gray-200 font-medium'
                      : 'hover:bg-gray-100'
                    }`}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={checkGuess} className="med-primary-solid med-bx-button">
            Submit Guess
          </button>
          <button
            onClick={revealHint}
            disabled={revealedHints.length >= hints.length}
            className="med-primary-solid med-bx-button"
          >
            Get Hint ({hints.length - revealedHints.length} left)
          </button>
          <button
            onClick={() => (showAnswer ? getNewMedicine() : toggleShowAnswer())}
            className="med-primary-solid med-bx-button"
          >
            {showAnswer ? 'New Medicine' : 'Show Answer'}
          </button>
        </div>

        {showAnswer && (
          <p className="mt-2 font-bold text-green-600">
            Correct Answer: {answerField}
          </p>
        )}

        {revealedHints.length > 0 && (
          <div className="mt-4">
            <p className="font-bold mb-2">Revealed Hints:</p>
            <ul className="list-disc pl-6">
              {revealedHints.map((hint, index) => (
                <li key={index} className="text-sm">
                  {hint}
                </li>
              ))}
            </ul>
          </div>
        )}

        <h3>Current Mode: {currentMode.emoji} {currentMode.label}</h3>
        <div>
          {Object.entries(modes).map(([key, mode]) => (
            <button
              key={key}
              onClick={() => toggleMode(key)}
              disabled={key === 'EMRD_SELECTION' && selectedCount === 0}
              className={`med-primary-solid med-bx-button ${activeMode === key ? 'border-2 border-indigo-500' : ''
                }`}
            >
              {mode.label} {mode.emoji} {key === 'EMRD_SELECTION' ? `(${selectedCount})` : ''}
            </button>
          ))}

          <button
            onClick={toggleGuessType}
            className="med-primary-solid med-bx-button"
          >
            {guessType === 'brand'
              ? 'Switch to guess INN 🧪'
              : 'Switch to guess Brand 💊'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { MedicineGuessGame };